/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import styled from '@emotion/styled';
import { css } from '@emotion/react';

import {
  CDS_SIDEBAR_DESKTOP_SUBMENU_WIDTH_PX,
  CDS_SIDEBAR_MOBILE_NAV_WIDTH_PX,
  CDS_SIDEBAR_RAIL_WIDTH_PX,
} from './sidebarNavConstants';

const surfaceBg = theme =>
  theme.validateMode({
    light: '#F8FDFF',
    dark: '#1C1B1F',
  });

const borderColor = theme =>
  theme.validateMode({
    light: '#CAC4D0',
    dark: '#49454F',
  });

const hoverBg = theme =>
  theme.validateMode({
    light: theme.colorsCA.darkBlue[95],
    dark: theme.colorsCA.neutral[30],
  });

const activeBg = theme =>
  theme.validateMode({
    light: theme.colorsCA.darkBlue[30],
    dark: theme.colorsCA.darkBlue[60],
  });

const mutedText = theme =>
  theme.validateMode({
    light: theme.colorsCA.neutral[40],
    dark: theme.colorsCA.neutral[95],
  });

const focusOutlineColor = theme => theme.validateMode({ light: theme.colorsCA.darkBlue[40], dark: '#D0BCFF' });

const elevatedShadow = theme =>
  theme.validateMode({
    light: 'box-shadow: 4px 0px 8px 0px #65778833;',
    dark: theme.elevationCA[3],
  });

const scrollbarStyles = theme => css`
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 3px;
    transition: background 0.2s ease;
  }

  &:hover::-webkit-scrollbar-thumb,
  &:focus::-webkit-scrollbar-thumb,
  &:active::-webkit-scrollbar-thumb {
    background: ${borderColor(theme)};
  }

  &:hover::-webkit-scrollbar-thumb:hover {
    background: ${theme.validateMode({ light: '#9CA3AF', dark: '#6B7280' })};
  }
`;

export const SidebarContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    flex: 0 0 ${CDS_SIDEBAR_RAIL_WIDTH_PX}px;
    width: ${CDS_SIDEBAR_RAIL_WIDTH_PX}px;
    overflow: hidden;
    border-radius: 0 10px 10px 0;

    &.is-mobile {
      margin-right: 10px;
    }
    z-index: 995;
    height: 100vh;
    max-height: 100vh;

    background-color: ${surfaceBg(theme)};
    border-right: 1px solid ${borderColor(theme)};
    ${elevatedShadow(theme)}

    &.rail-no-hover:not(.is-mobile) {
      .sidebar-options-container .option {
        transition: background-color 0.18s ease, color 0.18s ease;
        position: relative;

        &:hover {
          background-color: ${hoverBg(theme)};

          .tooltip {
            visibility: visible;
            opacity: 1;
            transform: translateX(0);
            transition: opacity 0.18s ease 0.5s, transform 0.18s ease 0.5s, visibility 0s linear 0.5s;
          }
        }

        &.option__active:hover {
          background-color: ${activeBg(theme)};
          color: ${theme.colorsCA.neutral[95]};
        }
      }
    }

    .sidebar-logo-container {
      display: flex;
      justify-content: center;
      height: 107px;
      padding-top: 16px;
      flex-shrink: 0;

      &.is-mobile {
        justify-content: space-between;
        height: auto;
        align-items: center;
        padding: 12px 12px 12px 22px;
        margin-inline: -10px;
        border-bottom: 1px solid ${borderColor(theme)};
      }

      .app-icon {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        text-align: center;
        width: 40px;
        height: 40px;
        cursor: default;
        font-size: 10px;
        font-weight: 500;
        line-height: 10px;
        background-color: #ffffff;
        border: 1px solid ${theme.validateMode({ light: '#E6E1E5', dark: '#938f99' })};
        border-radius: 10px;
        transition: background-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;

        &:hover {
          background-color: #fffbfe;
        }

        &.app-icon--opens-drawer {
          cursor: pointer;

          &:hover {
            box-shadow: 0 2px 8px rgba(0, 43, 84, 0.18);
            transform: translateY(-1px);
          }

          &:focus-visible {
            outline: 2px solid ${focusOutlineColor(theme)};
            outline-offset: 2px;
          }
        }

        img {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 30px;
          object-fit: contain;
        }
      }
    }

    .sidebar-options-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      overflow-y: auto;
      overflow-x: hidden;
      flex: 1;
      max-height: calc(100vh - 107px);

      ${scrollbarStyles(theme)}

      &.hidden {
        display: none;
      }

      .option {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 40px;
        height: 40px;
        border-radius: 10px;
        margin-top: 11px;
        margin-bottom: 11px;
        cursor: pointer;
        color: ${mutedText(theme)};
        transition: background-color 0.18s ease, color 0.18s ease;

        &:hover {
          background-color: ${hoverBg(theme)};
        }

        &__active {
          box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.18) inset;
          color: ${theme.colorsCA.neutral[95]};
          background-color: ${activeBg(theme)};

          &:hover {
            background-color: ${activeBg(theme)};
          }
        }
      }

      .icon {
        height: 20px;
      }

      .rail-parent-chevron {
        position: absolute;
        right: 3px;
        bottom: 3px;
        line-height: 1;
        pointer-events: none;
        opacity: 0.45;
        transition: transform 0.22s ease, opacity 0.18s ease;

        &.rail-parent-chevron--open {
          transform: rotate(180deg);
          opacity: 0.85;
        }

        &.MuiSvgIcon-root,
        .MuiSvgIcon-root {
          font-size: 12px;
          width: 12px;
          height: 12px;
        }
      }

      .option:hover .rail-parent-chevron,
      .option__active .rail-parent-chevron {
        opacity: 0.85;
      }

      button.option {
        font: inherit;
        color: ${mutedText(theme)};
        padding: 0;
        margin-top: 11px;
        margin-bottom: 11px;
        border: none;
        background-color: transparent;
        cursor: pointer;
        position: relative;

        &:hover {
          background-color: ${hoverBg(theme)};
        }

        &.option__active {
          background-color: ${activeBg(theme)};
          color: ${theme.colorsCA.neutral[95]};
          box-shadow: 0px 4px 4px 0px #00000040 inset;

          &:hover {
            background-color: ${activeBg(theme)};
          }
        }

        &:focus-visible {
          outline: 2px solid ${focusOutlineColor(theme)};
          outline-offset: 2px;
        }
      }

      .tooltip {
        visibility: hidden;
        opacity: 0;
        transform: translateX(-4px);
        transition: opacity 0.15s ease, transform 0.15s ease, visibility 0s linear 0.15s;
        position: absolute;
        left: ${CDS_SIDEBAR_RAIL_WIDTH_PX + 12}px;
        padding: 11px 16px;
        border-radius: 10px;
        border: 1px solid ${borderColor(theme)};
        background: ${theme.validateMode({
          light: 'rgba(255, 255, 255, 0.85)',
          dark: 'rgba(74, 70, 81, 0.85)',
        })};
        box-shadow: 0px 4px 20px 0px
          ${theme.validateMode({ light: 'rgba(101, 119, 136, 0.2)', dark: 'rgba(0, 0, 0, 0.30)' })};
        backdrop-filter: blur(16px);
        color: ${mutedText(theme)};
        font-size: 14px;
        font-weight: 600;
        white-space: nowrap;
        z-index: 999;
      }
    }

    &.sidebar-secondary {
      background: ${theme.validateMode({
        light: '#FCFFF4',
        dark: 'linear-gradient(0deg, rgba(217, 255, 188, 0.12) 0%, rgba(217, 255, 188, 0.12) 100%), #1C1B1F',
      })};

      .sidebar-options-container .option.option__active {
        background-color: #83aa32;
      }

      .menu-sidebar {
        background: ${theme.validateMode({
          light: '#FCFFF4',
          dark: 'linear-gradient(0deg, rgba(217, 255, 188, 0.12) 0%, rgba(217, 255, 188, 0.12) 100%), #1C1B1F',
        })};

        .menu-order-container .menu-order-icon__active {
          background-color: #83aa32;
        }

        .menu-options-container .menu-option__active {
          background-color: #83aa32;
        }
      }
    }

    &.is-mobile {
      flex-basis: unset;
      height: 100%;
      padding: 10px;
      width: ${CDS_SIDEBAR_MOBILE_NAV_WIDTH_PX}px;
      border-radius: 0 12px 12px 0;

      .menu-icon {
        cursor: pointer;
      }

      .sidebar-options-container {
        align-items: stretch;
        margin-top: 12px;
        gap: 6px;
        padding: 0 4px;
        max-height: calc(100vh - 120px);

        ${scrollbarStyles(theme)}

        .option {
          width: 100%;
          height: 44px;
          border-radius: 999px;
          align-items: center;
          padding: 0 14px;
          color: ${mutedText(theme)};

          &:hover {
            background-color: ${hoverBg(theme)};
          }

          .label {
            color: ${mutedText(theme)};
            font-family: 'Work Sans';
            font-size: 14px;
            font-weight: 600;
            line-height: 20px;
          }

          &__active {
            box-shadow: none;
            background-color: ${activeBg(theme)};
            color: ${theme.colorsCA.neutral[95]};

            .label {
              color: ${theme.colorsCA.neutral[95]};
            }

            &:hover {
              background-color: ${activeBg(theme)};
            }
          }
        }
      }
    }
  `}
`;

export const NavCluster = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  height: 100%;
  flex-shrink: 0;
  position: relative;
  margin-right: 10px;
`;

export const PushSubmenuPanel = styled.aside`
  ${({ theme, $isOpen }) => css`
    display: flex;
    flex-direction: column;
    width: ${$isOpen ? `${CDS_SIDEBAR_DESKTOP_SUBMENU_WIDTH_PX}px` : '0'};
    min-width: 0;
    overflow: hidden;
    transition: width 0.24s cubic-bezier(0.2, 0, 0, 1);
    height: 100vh;
    max-height: 100vh;
    flex-shrink: 0;
    border-right: ${$isOpen ? `1px solid ${borderColor(theme)}` : 'none'};
    background-color: ${$isOpen ? surfaceBg(theme) : 'transparent'};
    ${$isOpen ? elevatedShadow(theme) : ''}

    .push-panel-inner {
      display: flex;
      flex-direction: column;
      min-height: 0;
      flex: 1;
      width: ${CDS_SIDEBAR_DESKTOP_SUBMENU_WIDTH_PX}px;
    }

    .push-panel-header {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 14px 12px 10px 16px;
      flex-shrink: 0;
      border-bottom: 1px solid ${theme.validateMode({ light: '#E6E1E5', dark: '#49454F' })};
    }

    .push-panel-title {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 10px;
      min-width: 0;
      flex: 1;
      color: ${mutedText(theme)};

      .MuiSvgIcon-root {
        width: 22px;
        height: 22px;
        flex-shrink: 0;
      }

      span {
        font-size: 16px;
        font-weight: 700;
        line-height: 1.2;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .push-panel-actions {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 4px;
      flex-shrink: 0;
    }

    .push-panel-icon-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      padding: 0;
      border: none;
      border-radius: 10px;
      cursor: pointer;
      background: transparent;
      color: ${mutedText(theme)};
      transition: background-color 0.16s ease;

      &:focus-visible {
        outline: 2px solid ${focusOutlineColor(theme)};
        outline-offset: 2px;
      }

      &:hover {
        background-color: ${hoverBg(theme)};
      }
    }

    .push-panel-body {
      display: flex;
      flex-direction: column;
      min-height: 0;
      flex: 1;
      overflow: hidden;
    }

    .push-panel-body-scroll {
      display: flex;
      flex-direction: column;
      min-height: 0;
      flex: 1;
      overflow: auto;

      ${scrollbarStyles(theme)}
    }

    .push-panel-footer {
      flex-shrink: 0;
      padding: 8px 12px 12px;
      font-size: 12px;
      color: ${theme.validateMode({ light: theme.colorsCA.neutral[40], dark: theme.colorsCA.neutral[80] })};
    }

    &.submenu--push-desktop {
      .menu-search-container {
        display: flex;
        margin: 12px 12px 0;
      }

      .menu-order-container {
        display: flex;
        min-height: 38px;
        width: 120px;
        border: 1px solid ${borderColor(theme)};
        border-radius: 999px;
        margin: 12px 12px 0;
        overflow: hidden;
        padding: 0;
        background: transparent;

        .menu-order-icon {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 50%;
          background: transparent;
          border: none;
          cursor: pointer;
          color: ${mutedText(theme)};

          .MuiSvgIcon-root {
            font-size: 18px;
          }

          &:hover {
            background-color: ${hoverBg(theme)};
          }

          &:focus-visible {
            outline: 2px solid ${focusOutlineColor(theme)};
            outline-offset: -2px;
          }

          &:not(:first-of-type) {
            border-left: 1px solid ${borderColor(theme)};
          }

          &__active {
            box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.18) inset;
            color: ${theme.colorsCA.neutral[95]};
            pointer-events: none;
            background-color: ${activeBg(theme)};
          }
        }
      }

      .menu-options-container {
        display: flex;
        flex-direction: column;
        margin: 8px 0 0;
        padding: 0 12px 16px;
        gap: 2px;
        flex: 1;
        min-height: 0;
      }

      .menu-favorites-header {
        padding: 8px 12px 4px;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: ${mutedText(theme)};

        span {
          font: inherit;
          color: inherit;
        }
      }

      .menu-favorites-separator {
        margin: 8px 12px;
        border-top: 1px solid ${borderColor(theme)};
      }

      .menu-options-container .menu-option {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 10px;
        min-width: 0;
        width: 100%;
        box-sizing: border-box;
        margin: 0;
        padding: 10px 12px;
        border: none;
        border-radius: 10px;
        background: transparent;
        appearance: none;
        cursor: pointer;
        font: inherit;
        text-align: left;
        color: ${mutedText(theme)};
        transition: background-color 0.16s ease, color 0.16s ease;

        &:hover {
          background-color: ${hoverBg(theme)};
        }

        &:focus-visible {
          outline: 2px solid ${focusOutlineColor(theme)};
          outline-offset: 2px;
        }

        &__active {
          box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.18) inset;
          color: ${theme.colorsCA.neutral[95]};
          pointer-events: none;
          background-color: ${activeBg(theme)};
        }

        .MuiSvgIcon-root {
          flex-shrink: 0;
        }

        .menu-option-label {
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 14px;
          font-weight: 600;
        }

        .menu-option-badge {
          flex-shrink: 0;
          font-size: 12px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 999px;
          background: ${hoverBg(theme)};
        }
      }
    }
  `}
`;

export const MobileOptionRow = styled.div`
  cursor: pointer;
`;

export const MobileOptionContent = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding-left: 10px;

  .mobile-option-label {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 10px;
  }
`;

export const MobileBackOption = styled.div`
  display: flex;
  padding: 22px 0 0 32px;
  gap: 10px;
  align-items: center;
  cursor: pointer;
`;

export const MobileSubMenuContainer = styled.div`
  ${({ theme }) => css`
    .menu-sidebar {
      max-height: 92vh;
      width: 100%;
      padding: 16px 0 0;
      border: none;
      display: flex;
      flex-direction: column;
      height: 100%;
      z-index: 999;
      left: 0;
      border-radius: 0;

      &.hidden {
        display: none;
      }

      .menu-title-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        margin: 12px 0 0;
        padding: 0 18px;

        .MuiSvgIcon-root {
          width: 24px;
          height: 24px;
        }

        span {
          margin-left: 10px;
          font-family: 'Work Sans';
          font-size: 16px;
          font-weight: 700;
          line-height: 20px;
          text-align: left;
        }
      }

      .menu-order-container {
        display: flex;
        min-height: 40px;
        width: 120px;
        border: 1px solid ${borderColor(theme)};
        border-radius: 999px;
        margin: 20px 18px 0;
        overflow: hidden;
        padding: 0;
        background: transparent;

        .menu-order-icon {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 50%;
          background: transparent;
          border: none;
          cursor: pointer;
          color: ${mutedText(theme)};

          .MuiSvgIcon-root {
            font-size: 18px;
          }

          &:hover {
            background-color: ${hoverBg(theme)};
          }

          &:not(:first-of-type) {
            border-left: 1px solid ${borderColor(theme)};
          }

          &__active {
            color: ${theme.colorsCA.neutral[95]};
            pointer-events: none;
            background-color: ${activeBg(theme)};
          }
        }
      }

      .menu-search-container {
        display: flex;
        margin: 20px 18px 0;
      }

      .menu-options-container {
        display: flex;
        flex-direction: column;
        overflow: auto;
        margin: 12px 0;
        padding: 0 18px;
        gap: 8px;
      }

      .menu-favorites-header {
        padding: 4px 4px 0;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: ${mutedText(theme)};

        span {
          font: inherit;
          color: inherit;
        }
      }

      .menu-favorites-separator {
        margin: 4px 4px;
        border-top: 1px solid ${borderColor(theme)};
      }

      .menu-option {
        padding: 12px 14px;
        border-radius: 999px;
        cursor: pointer;
        display: flex;
        height: 44px;
        align-items: center;
        color: ${mutedText(theme)};
        transition: background-color 0.16s ease;

        &:hover {
          background-color: ${hoverBg(theme)};
        }

        &__active {
          color: ${theme.colorsCA.neutral[95]};
          pointer-events: none;
          background-color: ${activeBg(theme)};

          &.is-mobile {
            box-shadow: none;
          }
        }

        span {
          font-size: 14px;
          font-weight: 500;
        }

        &.is-mobile span {
          font-family: 'Work Sans';
          font-size: 14px;
          font-weight: 600;
          line-height: 20px;
          text-align: left;
        }
      }
    }
  `}
`;
